import React, { useState, useCallback, useMemo } from 'react';
import { Modal, Box, SpaceBetween, Button, Alert, Select, FormField, Input, RadioGroup, Pagination } from '@amzn/awsui-components-core';
import { Avatar, AvatarSize } from '@amzn/stencil-react-components/avatar';
import { EmployeePopoverCard } from '../../components/attendees/EmployeePopoverCard';
import { PRIORITY_COLORS } from '../../constants/EAHuddleConstants';
import { RecentMeeting, MeetingAttendee, ResolvedMatch } from '../../interfaces/interface';
import { EmployeeSettingsService } from '../../services/EmployeeSettingsService';
import { logger } from 'src/logger';

interface ImportFromMeetingModalProps {
  visible: boolean;
  onDismiss: () => void;
  recentMeetings: RecentMeeting[];
  resolvedAttendees: ResolvedMatch[];
  onImportComplete: (attendees: MeetingAttendee[]) => void;
}

export const ImportFromMeetingModal: React.FC<ImportFromMeetingModalProps> = ({
  visible,
  onDismiss,
  recentMeetings,
  resolvedAttendees,
  onImportComplete
}) => {
  const [selectedMeetingId, setSelectedMeetingId] = useState<string | null>(null);
  const [copyError, setCopyError] = useState('');
  const [meetingUrlInput, setMeetingUrlInput] = useState('');
  const [importMode, setImportMode] = useState<'recent' | 'url'>('recent');
  const [copyAttendeePage, setCopyAttendeePage] = useState(1);
  const [urlMeeting, setUrlMeeting] = useState<{ attendees: MeetingAttendee[]; subject: string } | null>(null);
  const [isLoadingUrl, setIsLoadingUrl] = useState(false);

  const selectedMeeting = recentMeetings.find((m) => m.meetingId === selectedMeetingId) ?? null;

  const copyExistingLogins = useMemo(
    () => new Set(resolvedAttendees.map((a) => (a.selectedEmployee?.login_name || a.inputName).toLowerCase())),
    [resolvedAttendees]
  );

  const handleClose = useCallback(() => {
    setSelectedMeetingId(null);
    setMeetingUrlInput('');
    setUrlMeeting(null);
    setImportMode('recent');
    setCopyError('');
    setCopyAttendeePage(1);
    onDismiss();
  }, [onDismiss]);

  const handleSubmit = useCallback(() => {
    const meetingAttendees = importMode === 'recent' ? selectedMeeting?.attendees : urlMeeting?.attendees;
    if (!meetingAttendees) return;

    const existingLogins = new Set(resolvedAttendees.map((a) => (a.selectedEmployee?.login_name || a.inputName).toLowerCase()));
    const newAttendees = meetingAttendees.filter((a) => !existingLogins.has(a.loginName.toLowerCase()));

    handleClose();
    onImportComplete(newAttendees);
  }, [importMode, selectedMeeting, urlMeeting, resolvedAttendees, handleClose, onImportComplete]);

  const handleFetchFromUrl = useCallback(async () => {
    if (!meetingUrlInput.trim()) return;
    setCopyError('');
    setIsLoadingUrl(true);
    try {
      const url = new URL(meetingUrlInput.trim());
      const params = url.searchParams;
      const urlMeetingId = params.get('meetingId');
      const urlCreator = params.get('creator');
      if (!urlMeetingId || !urlCreator) {
        setCopyError('Invalid meeting URL. Expected format: ...?meetingId=<id>&creator=<alias>');
        return;
      }
      const meetingData = await EmployeeSettingsService.getMeeting(urlCreator, urlMeetingId);
      if (!meetingData) {
        setCopyError('Meeting not found. The link may be invalid or expired.');
        return;
      }
      setUrlMeeting({ attendees: meetingData.attendees, subject: meetingData.subject });
      setCopyAttendeePage(1);
    } catch (error) {
      logger.error('Failed to fetch meeting from URL:', { error });
      setCopyError('Failed to load meeting from URL. Please check the link and try again.');
    } finally {
      setIsLoadingUrl(false);
    }
  }, [meetingUrlInput]);

  if (!visible) return null;

  const displayAttendees = importMode === 'recent' ? selectedMeeting?.attendees : urlMeeting?.attendees;
  const displaySubject = importMode === 'recent' ? selectedMeeting?.subject : urlMeeting?.subject;

  return (
    <Modal
      visible={true}
      onDismiss={handleClose}
      header="Import from Meeting"
      size="medium"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={handleClose}>
              Cancel
            </Button>
            <span className="blue-button">
              <Button variant="primary" onClick={handleSubmit} disabled={importMode === 'recent' ? !selectedMeeting : !urlMeeting}>
                Load Attendees
              </Button>
            </span>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween direction="vertical" size="m">
        {copyError && (
          <Alert statusIconAriaLabel="Error" type="error">
            {copyError}
          </Alert>
        )}
        <RadioGroup
          value={importMode}
          onChange={({ detail }) => {
            setImportMode(detail.value as 'recent' | 'url');
            setCopyError('');
            setCopyAttendeePage(1);
          }}
          items={[
            { value: 'recent', label: 'Select from Recent Meetings' },
            { value: 'url', label: 'Import from Meeting URL' }
          ]}
        />

        {importMode === 'recent' && (
          <FormField label="Select a Meeting">
            <Select
              selectedOption={selectedMeeting ? { label: selectedMeeting.subject, value: selectedMeeting.meetingId } : null}
              onChange={({ detail }) => {
                setSelectedMeetingId(detail.selectedOption.value ?? null);
                setCopyAttendeePage(1);
              }}
              options={recentMeetings.map((m) => ({
                label: m.subject,
                value: m.meetingId,
                description: `${m.attendees.length} attendees · ${new Date(m.createdAt).toLocaleDateString()}`
              }))}
              placeholder="Choose a recent meeting..."
              filteringType="auto"
            />
          </FormField>
        )}

        {importMode === 'url' && (
          <FormField label="Meeting URL">
            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <Input value={meetingUrlInput} onChange={({ detail }) => setMeetingUrlInput(detail.value)} placeholder="Paste meeting URL..." />
              </div>
              <Button onClick={handleFetchFromUrl} loading={isLoadingUrl} disabled={!meetingUrlInput.trim()}>
                Look Up
              </Button>
            </div>
          </FormField>
        )}

        {displayAttendees && (
          <SpaceBetween direction="vertical" size="xs">
            {displaySubject && (
              <>
                <Box variant="h2">{displaySubject}</Box>
                <hr style={{ border: 'none', borderTop: '1px solid #e9ebed', margin: '4px 0' }} />
              </>
            )}
            <Box fontWeight="bold" fontSize="body-s">
              Attendees ({displayAttendees.length})
            </Box>
            {displayAttendees.slice((copyAttendeePage - 1) * 10, copyAttendeePage * 10).map((attendee) => {
              const isAlreadyAdded = copyExistingLogins.has(attendee.loginName.toLowerCase());
              return (
                <div key={attendee.loginName} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <EmployeePopoverCard loginName={attendee.loginName} renderWithPortal>
                    <Avatar username={attendee.loginName} size={AvatarSize.ExtraSmall} imageAltText={attendee.loginName} />
                  </EmployeePopoverCard>
                  <Box fontSize="body-s" fontWeight="bold">
                    {attendee.employeeName || attendee.loginName}
                  </Box>
                  <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span
                      className="priority-badge"
                      style={{ backgroundColor: PRIORITY_COLORS[attendee.priority]?.background, color: PRIORITY_COLORS[attendee.priority]?.color }}
                    >
                      {attendee.priority.charAt(0).toUpperCase() + attendee.priority.slice(1)}
                    </span>
                    {isAlreadyAdded && <span className="priority-badge added-badge">Already Added</span>}
                  </div>
                </div>
              );
            })}
            {Math.ceil(displayAttendees.length / 10) > 1 && (
              <Pagination
                currentPageIndex={copyAttendeePage}
                pagesCount={Math.ceil(displayAttendees.length / 10)}
                onChange={({ detail }) => setCopyAttendeePage(detail.currentPageIndex)}
              />
            )}
          </SpaceBetween>
        )}
      </SpaceBetween>
    </Modal>
  );
};
